import { Box, Typography } from '@mui/material';
import { Pencil } from 'lucide-react';
import type { ReactElement } from 'react';

import type { AdminPrivacyRequest } from '@/admin/adminApi';
import { accountDetailPath } from '@/admin/users/routes';
import { ProgressBar } from '@/ui/charts';
import { Badge, Button, DataTable, type DataTableColumn } from '@/ui/kit';

import {
  STATUS_LABELS,
  STATUS_TONES,
  TYPE_LABELS,
  TYPE_TONES,
  deadlineLabel,
  deadlineTone,
  formatDay,
} from './privacyRequestsModel';

/**
 * Le registre : type, personne, reception, echeance avec sa barre, statut,
 * responsable.
 *
 * **La barre ne porte jamais seule l'etat** : la phrase de l'echeance (« dans
 * 26 j », « en retard de 3 j », « clôturée dans le délai ») et, pour un retard, la
 * pastille « En retard » le disent en toutes lettres. Les trois valeurs viennent
 * du serveur ; ce tableau n'en calcule aucune.
 *
 * La colonne d'action n'existe que pour `admin` : un `support` lit, il n'ecrit
 * pas, et un bouton grise sans explication se lirait comme une panne — l'ecran
 * dit pourquoi, au-dessus du tableau.
 */
export const RequestsTable = ({
  items,
  canWrite,
  onEdit,
}: {
  readonly items: readonly AdminPrivacyRequest[];
  readonly canWrite: boolean;
  readonly onEdit: (request: AdminPrivacyRequest) => void;
}): ReactElement => {
  const columns: DataTableColumn<AdminPrivacyRequest>[] = [
    {
      key: 'type',
      header: 'Type',
      primary: true,
      render: (request) => <Badge label={TYPE_LABELS[request.type]} tone={TYPE_TONES[request.type]} />,
    },
    {
      key: 'person',
      header: 'Personne',
      render: (request) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography component="div" sx={{ fontSize: 14, fontWeight: 700, overflowWrap: 'anywhere' }}>
            {request.requesterEmail}
          </Typography>
          {request.userId === null ? (
            <Typography component="div" sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
              Aucun compte actif
            </Typography>
          ) : (
            <Button
              variant="ghost"
              size="S"
              to={accountDetailPath(request.userId)}
              aria-label={`Ouvrir la fiche du compte de ${request.requesterEmail}`}
            >
              Fiche du compte
            </Button>
          )}
        </Box>
      ),
    },
    { key: 'received', header: 'Reçue le', render: (request) => formatDay(request.receivedAt) },
    {
      key: 'due',
      header: 'Échéance',
      render: (request) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 140 }}>
          <Typography component="div" sx={{ fontSize: 13, fontWeight: 700 }}>
            {deadlineLabel(request)}
          </Typography>
          {request.status === 'closed' ? null : (
            <ProgressBar label="Part du délai écoulée" value={request.elapsedPercent} tone={deadlineTone(request)} width={120} />
          )}
          <Typography component="div" sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
            {request.status === 'closed' ? `Clôturée le ${formatDay(request.closedAt)}` : `Le ${formatDay(request.dueAt)}`}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'status',
      header: 'Statut',
      render: (request) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
          <Badge label={STATUS_LABELS[request.status]} tone={STATUS_TONES[request.status]} dot={request.status !== 'closed'} />
          {request.overdue ? <Badge label="En retard" tone="danger" /> : null}
        </Box>
      ),
    },
    {
      key: 'assignee',
      header: 'Responsable',
      // Sans responsable : `null`, que le tableau ecrit « — » (un element vide ne le serait pas).
      render: (request) =>
        request.assigneeEmail === null ? null : (
          <Box component="span" sx={{ overflowWrap: 'anywhere', color: 'tokens.textMuted', fontSize: 13 }}>
            {request.assigneeEmail}
          </Box>
        ),
    },
  ];

  if (canWrite) {
    columns.push({
      key: 'actions',
      header: 'Action',
      align: 'right',
      render: (request) =>
        request.status === 'closed' ? (
          <Typography component="span" sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
            Non modifiable
          </Typography>
        ) : (
          <Button
            size="S"
            icon={Pencil}
            aria-label={`Faire évoluer la demande ${TYPE_LABELS[request.type].toLowerCase()} de ${request.requesterEmail}`}
            onClick={() => {
              onEdit(request);
            }}
          >
            Faire évoluer
          </Button>
        ),
    });
  }

  return (
    <DataTable
      label="Demandes d’exercice de droits"
      columns={columns}
      rows={items}
      getRowId={(request) => request.id}
      emptyMessage="Aucune demande ne correspond à ce filtre."
    />
  );
};
